import { Box, Button, Collapse, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowDown2,
  Clock,
  Edit2,
  Location,
  Lock1,
  MessageText1,
  Mobile,
  ShieldTick,
  TickCircle,
  Shop,
  Truck,
  TruckFast,
  WalletCheck,
  type Icon as IconsaxIcon,
} from "iconsax-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ProductIconTile } from "@/components/Atoms";
import { FloatingTextField, LocationPicker, OtpInput, SefanoButton } from "@/components/Molecules";
import { CHECKOUT_STEPS, type CheckoutStep } from "@/hooks/useCheckoutPage";
import { formatPersianCurrency, formatPersianNumber } from "@/lib/format/persian";
import { resolveMediaUrl } from "@/lib/media";
import type { CheckoutSummaryProps, CheckoutTemplateProps } from "./Checkout.template.types";
import * as styles from "./Checkout.template.styles";

export type { CheckoutTemplateProps } from "./Checkout.template.types";

const SHIPPING_METHOD_ICONS: Record<string, IconsaxIcon> = {
  post: Truck,
  courier: TruckFast,
  pickup: Shop,
};

const STEP_ICONS: Record<CheckoutStep, IconsaxIcon> = {
  phone: Mobile,
  otp: MessageText1,
  details: Location,
  payment: WalletCheck,
};

function CheckoutHeader() {
  const { t } = useTranslation("checkout");
  const theme = useTheme();

  return (
    <Box component="header" sx={styles.header}>
      <Box component="img" src="/images/nav_logo.png" alt="SEFANO" sx={styles.headerLogo} />
      <Box sx={styles.secureBadge}>
        <Lock1 size={14} variant="Bold" color={theme.palette.success.main} />
        {t("secureBadge")}
      </Box>
    </Box>
  );
}

function StoreLogo({
  name,
  logoUrl,
  size = 52,
}: {
  name: string;
  logoUrl?: string;
  size?: number;
}) {
  const src = resolveMediaUrl(logoUrl);
  const [failed, setFailed] = useState(false);

  return src && !failed ? (
    <Box
      component="img"
      src={src}
      alt={name}
      onError={() => setFailed(true)}
      sx={styles.storeLogo(size)}
    />
  ) : (
    <Box sx={styles.storeLogoFallback(size)}>{name.trim().charAt(0) || "ف"}</Box>
  );
}

function StatusScreen({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <Box sx={styles.root}>
      <CheckoutHeader />
      <Box component="main" sx={styles.main}>
        <Box sx={styles.mainInner}>
          <Box sx={styles.statusCard}>
            {icon}
            <Typography sx={styles.statusTitle}>{title}</Typography>
            {description ? (
              <Typography sx={styles.statusDescription}>{description}</Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Steps with a handler in `targets` are buttons. A step after the current one can
 * still be reachable (e.g. back to payment while editing details); it shows as done.
 */
function CheckoutStepper({
  stepIndex,
  targets,
}: {
  stepIndex: number;
  targets: Partial<Record<CheckoutStep, () => void>>;
}) {
  const { t } = useTranslation("checkout");

  return (
    <Box component="ol" sx={styles.stepper}>
      {CHECKOUT_STEPS.map((step, index) => {
        const onSelect = index === stepIndex ? undefined : targets[step];
        const state =
          index === stepIndex ? "current" : index < stepIndex || onSelect ? "done" : "upcoming";
        const label = t(`steps.${step}`);
        const content = (
          <>
            <Box sx={styles.stepDot(state)}>
              {state === "done" ? "✓" : formatPersianNumber(index + 1)}
            </Box>
            <Box component="span" sx={styles.stepLabel(state)}>
              {label}
            </Box>
          </>
        );

        return (
          <Box
            component="li"
            key={step}
            sx={styles.stepItem(state)}
            aria-current={state === "current" ? "step" : undefined}
          >
            {onSelect ? (
              <Box
                component="button"
                type="button"
                onClick={onSelect}
                title={t("stepper.goTo", { step: label })}
                sx={styles.stepButton}
              >
                {content}
              </Box>
            ) : (
              <Box sx={styles.stepStatic}>{content}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function StepHeading({
  step,
  title,
  description,
}: {
  step: CheckoutStep;
  title: string;
  description?: ReactNode;
}) {
  const theme = useTheme();
  const Icon = STEP_ICONS[step];

  return (
    <Box sx={styles.stepHeading}>
      <Box sx={styles.stepIcon}>
        <Icon size={22} variant="Bulk" color={theme.palette.primary.main} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={styles.stepTitle}>{title}</Typography>
        {description ? <Typography sx={styles.stepDescription}>{description}</Typography> : null}
      </Box>
    </Box>
  );
}

/** A titled group of fields inside a step form. */
function FormSection({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box component="section" sx={styles.formSection}>
      <Box sx={styles.formSectionHeader}>
        <Typography component="h3" sx={styles.formSectionTitle}>
          {title}
        </Typography>
        {aside}
      </Box>
      {children}
    </Box>
  );
}

/** Primary action filling the row, with an optional quiet cancel beside it. */
function ActionRow({
  children,
  onCancel,
  cancelLabel,
}: {
  children: ReactNode;
  onCancel?: () => void;
  cancelLabel: string;
}) {
  return (
    <Box sx={styles.actionRow}>
      <Box sx={styles.actionPrimary}>{children}</Box>
      {onCancel ? (
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          fullWidth={false}
          onClick={onCancel}
          sx={styles.actionSecondary}
        >
          {cancelLabel}
        </Button>
      ) : null}
    </Box>
  );
}

function SummaryItems({ items }: { items: CheckoutSummaryProps["items"] }) {
  return (
    <Box sx={styles.itemList}>
      {items.map((item, index) => (
        <Box key={`${item.product_name}-${index}`} sx={styles.itemRow}>
          <Box sx={styles.itemThumb}>
            <ProductIconTile imageUrl={item.image_url} alt={item.product_name} size={52} />
            {item.quantity > 1 ? (
              <Box sx={styles.qtyBadge}>{formatPersianNumber(item.quantity)}</Box>
            ) : null}
          </Box>
          <Box sx={styles.itemText}>
            <Typography sx={styles.itemName}>{item.product_name}</Typography>
            <Typography sx={styles.itemMeta}>
              {formatPersianNumber(item.quantity)} × {formatPersianCurrency(item.unit_price)}
            </Typography>
          </Box>
          <Typography sx={styles.itemPrice}>{formatPersianCurrency(item.total_price)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function SummaryTotals({
  subtotal,
  shipping,
  discount,
  total,
}: Pick<CheckoutSummaryProps, "subtotal" | "shipping" | "discount" | "total">) {
  const { t } = useTranslation("checkout");

  return (
    <Box sx={styles.totals}>
      <Box sx={styles.payRow}>
        <Typography color="text.secondary" fontSize="inherit">
          {t("summary.subtotal")}
        </Typography>
        <Typography fontWeight={600} fontSize="inherit">
          {formatPersianCurrency(subtotal)}
        </Typography>
      </Box>
      <Box sx={styles.payRow}>
        <Typography color="text.secondary" fontSize="inherit">
          {t("summary.shipping")}
        </Typography>
        <Typography fontWeight={600} fontSize="inherit">
          {shipping > 0 ? formatPersianCurrency(shipping) : t("summary.freeShipping")}
        </Typography>
      </Box>
      {discount > 0 ? (
        <Box sx={styles.payRow}>
          <Typography color="text.secondary" fontSize="inherit">
            {t("summary.discount")}
          </Typography>
          <Typography fontWeight={600} fontSize="inherit" color="success.main">
            − {formatPersianCurrency(discount)}
          </Typography>
        </Box>
      ) : null}
      <Box sx={styles.divider} />
      <Box sx={styles.totalRow}>
        <Typography fontWeight={700}>{t("payment.total")}</Typography>
        <Typography sx={styles.totalAmount}>{formatPersianCurrency(total)}</Typography>
      </Box>
    </Box>
  );
}

function TrustRow() {
  const { t } = useTranslation("checkout");
  const theme = useTheme();
  const items = [
    { Icon: ShieldTick, label: t("trust.secure") },
    { Icon: Truck, label: t("trust.shipping") },
    { Icon: MessageText1, label: t("trust.tracking") },
  ];

  return (
    <Box sx={styles.trustRow}>
      {items.map(({ Icon, label }) => (
        <Box key={label} sx={styles.trustItem}>
          <Icon size={18} variant="Bulk" color={theme.palette.primary.main} />
          {label}
        </Box>
      ))}
    </Box>
  );
}

/** Desktop: sticky sidebar. Mobile: collapsible bar with a thumbnail strip. */
function SummaryCard(props: CheckoutSummaryProps) {
  const { t } = useTranslation("checkout");
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const itemCount = props.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      <Box sx={styles.summaryDesktop}>
        <Typography sx={styles.sectionTitle}>
          {t("summary.title")}
          <Box component="span" sx={styles.countChip}>
            {t("summary.itemsCount", { count: formatPersianNumber(itemCount) })}
          </Box>
        </Typography>
        <SummaryItems items={props.items} />
        <SummaryTotals {...props} />
        <TrustRow />
      </Box>

      <Box sx={styles.summaryMobile}>
        <Box
          component="button"
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
          sx={styles.summaryToggle}
        >
          <Box sx={styles.thumbStack}>
            {props.items.slice(0, 3).map((item, index) => (
              <Box key={`${item.product_name}-${index}`} sx={styles.thumbStackItem(index)}>
                <ProductIconTile imageUrl={item.image_url} alt={item.product_name} size={34} />
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: "start" }}>
            <Typography sx={styles.summaryToggleTitle}>
              {t(isOpen ? "summary.hide" : "summary.show")}
            </Typography>
            <Typography sx={styles.itemMeta}>
              {t("summary.itemsCount", { count: formatPersianNumber(itemCount) })}
            </Typography>
          </Box>
          <Typography sx={styles.summaryToggleTotal}>
            {formatPersianCurrency(props.total)}
          </Typography>
          <Box sx={styles.chevron(isOpen)}>
            <ArrowDown2 size={16} variant="Linear" color={theme.palette.text.secondary} />
          </Box>
        </Box>
        <Collapse in={isOpen} unmountOnExit>
          <Box sx={styles.summaryMobileBody}>
            <SummaryItems items={props.items} />
            <SummaryTotals {...props} />
          </Box>
        </Collapse>
      </Box>
    </>
  );
}

export function CheckoutTemplate({
  checkout,
  form,
  phoneForm,
  step,
  stepIndex,
  stepError,
  isLoading,
  isError,
  isUnavailable,
  isSubmitting,
  isPaying,
  isPaid,
  isProcessing,
  isRequestingOtp,
  isVerifyingOtp,
  otpSentTo,
  otpCode,
  otpLength,
  devOtpCode,
  resendSeconds,
  canResendOtp,
  verifiedPhone,
  savedCustomer,
  location,
  onLocationChange,
  filledFields,
  geocodeStatus,
  addressSuggestion,
  onApplyAddressSuggestion,
  onDismissAddressSuggestion,
  canCancelPhoneChange,
  canCancelDetailsEdit,
  showMobilePayDock,
  onSubmitPhone,
  onResendOtp,
  onOtpChange,
  onEditPhone,
  onCancelPhoneChange,
  onEditDetails,
  onCancelDetailsEdit,
  onSubmitCustomer,
  shippingMethods,
  shippingMethod,
  savedShippingMethod,
  onShippingMethodChange,
  handlePay,
}: CheckoutTemplateProps) {
  const { t } = useTranslation("checkout");
  const theme = useTheme();

  const renderPayButton = () => (
    <SefanoButton onClick={handlePay} disabled={isPaying} fullWidth sx={styles.payButton}>
      <Box component="span" sx={styles.payButtonContent}>
        <Lock1 size={18} variant="Bold" color={theme.palette.primary.contrastText} />
        {t("payment.payWithAmount", {
          amount: checkout ? formatPersianCurrency(checkout.final_payable_amount) : "",
        })}
      </Box>
    </SefanoButton>
  );

  const clockIcon = (
    <Box sx={styles.expiredIconWrap}>
      <Clock size={36} variant="Bold" color={theme.palette.warning.main} />
    </Box>
  );

  if (isLoading) {
    return <StatusScreen icon={<Box sx={styles.spinner(theme)} />} title={t("loading")} />;
  }

  if (isUnavailable) {
    return (
      <StatusScreen
        icon={clockIcon}
        title={t("unavailable")}
        description={t("unavailableDescription")}
      />
    );
  }

  if (isError || !checkout) {
    return (
      <StatusScreen icon={clockIcon} title={t("expired")} description={t("expiredDescription")} />
    );
  }

  if (isPaid) {
    return (
      <StatusScreen
        icon={
          <Box sx={styles.statusIconWrap("success")}>
            <TickCircle size={46} variant="Bold" color={theme.palette.success.main} />
          </Box>
        }
        title={t("payment.success")}
        description={t("payment.successDescription")}
      />
    );
  }

  if (isProcessing) {
    return (
      <StatusScreen
        icon={<Box sx={styles.spinner(theme)} />}
        title={t("payment.processing")}
        description={t("payment.processingHint")}
      />
    );
  }

  const errorBox = stepError ? (
    <Typography role="alert" sx={styles.stepError}>
      {stepError}
    </Typography>
  ) : null;

  const stepContent = {
    phone: (
      <>
        <StepHeading step="phone" title={t("phone.title")} description={t("phone.description")} />
        <Box component="form" onSubmit={onSubmitPhone} sx={styles.formStack}>
          <FloatingTextField
            label={t("phone.label")}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            autoFocus
            placeholder="09xxxxxxxxx"
            slotProps={{ htmlInput: { dir: "ltr", maxLength: 11 } }}
            error={Boolean(phoneForm.formState.errors.phone_number)}
            helperText={phoneForm.formState.errors.phone_number?.message}
            {...phoneForm.register("phone_number")}
          />
          {errorBox}
          <ActionRow
            onCancel={canCancelPhoneChange ? onCancelPhoneChange : undefined}
            cancelLabel={t("phone.cancel")}
          >
            <SefanoButton
              type="submit"
              disabled={isRequestingOtp}
              fullWidth
              sx={styles.primaryAction}
            >
              {isRequestingOtp ? t("phone.sending") : t("phone.submit")}
            </SefanoButton>
          </ActionRow>
        </Box>
      </>
    ),
    otp: (
      <>
        <StepHeading
          step="otp"
          title={t("otp.title")}
          description={
            <>
              {t("otp.description")}{" "}
              <Box component="span" dir="ltr" sx={{ fontWeight: 700, color: "text.primary" }}>
                {otpSentTo}
              </Box>{" "}
              <Box component="button" type="button" onClick={onEditPhone} sx={styles.inlineLink}>
                {t("otp.editPhone")}
              </Box>
            </>
          }
        />
        <Box sx={styles.otpWrap}>
          <OtpInput
            value={otpCode}
            onChange={onOtpChange}
            length={otpLength}
            disabled={isVerifyingOtp}
            error={Boolean(stepError)}
          />
        </Box>
        {errorBox}
        {isVerifyingOtp ? <Typography sx={styles.stepHint}>{t("otp.verifying")}</Typography> : null}
        {devOtpCode ? (
          <Box sx={styles.devCode}>{t("otp.devCode", { code: devOtpCode })}</Box>
        ) : null}
        <Box sx={styles.resendRow}>
          {canResendOtp ? (
            <Box component="button" type="button" onClick={onResendOtp} sx={styles.inlineLink}>
              {t("otp.resend")}
            </Box>
          ) : (
            <Typography sx={styles.stepHint}>
              {t("otp.resendIn", { seconds: formatPersianNumber(resendSeconds) })}
            </Typography>
          )}
        </Box>
      </>
    ),
    details: (
      <>
        <StepHeading
          step="details"
          title={t("customerInfo.title")}
          description={savedCustomer ? t("customerInfo.prefilled") : t("customerInfo.description")}
        />
        <Box component="form" onSubmit={onSubmitCustomer} sx={styles.formStack}>
          <FormSection title={t("customerInfo.recipientSection")}>
            <Box sx={styles.verifiedPhoneRow}>
              <TickCircle size={16} variant="Bold" color={theme.palette.success.main} />
              <Box component="span" sx={{ flex: 1 }}>
                {t("customerInfo.verifiedPhone")}{" "}
                <Box component="span" dir="ltr" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {verifiedPhone}
                </Box>
              </Box>
              <Box component="button" type="button" onClick={onEditPhone} sx={styles.inlineLink}>
                {t("otp.editPhone")}
              </Box>
            </Box>
            <FloatingTextField
              label={t("customerInfo.fullName")}
              autoComplete="name"
              error={Boolean(form.formState.errors.full_name)}
              helperText={form.formState.errors.full_name?.message}
              {...form.register("full_name")}
            />
          </FormSection>

          {shippingMethods.length > 0 ? (
            <FormSection title={t("shippingMethod.title")}>
              <Box role="radiogroup" aria-label={t("shippingMethod.title")} sx={styles.shippingMethodGrid}>
                {shippingMethods.map((method) => {
                  const MethodIcon = SHIPPING_METHOD_ICONS[method] ?? Truck;
                  const selected = method === shippingMethod;
                  return (
                    <Box
                      key={method}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onShippingMethodChange(method)}
                      sx={styles.shippingMethodOption(selected)}
                    >
                      <MethodIcon
                        size={22}
                        variant={selected ? "Bold" : "Linear"}
                        color={selected ? theme.palette.primary.main : theme.palette.text.secondary}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={styles.shippingMethodTitle}>{t(`shippingMethod.${method}`)}</Typography>
                        <Typography sx={styles.shippingMethodDesc}>{t(`shippingMethod.${method}Desc`)}</Typography>
                      </Box>
                      {selected ? <TickCircle size={18} variant="Bold" color={theme.palette.primary.main} /> : null}
                    </Box>
                  );
                })}
              </Box>
            </FormSection>
          ) : null}

          <FormSection
            title={t("customerInfo.addressSection")}
            aside={
              <Typography
                sx={styles.mapStatus(
                  geocodeStatus === "error" ? "error" : location ? "ok" : "idle",
                )}
              >
                {geocodeStatus === "idle"
                  ? location
                    ? t("map.picked")
                    : t("map.optional")
                  : t(`map.geocode.${geocodeStatus}`)}
              </Typography>
            }
          >
            <LocationPicker value={location} onChange={onLocationChange} height={200} />
            {addressSuggestion ? (
              <Box sx={styles.suggestionBox}>
                <Typography sx={styles.suggestionTitle}>{t("map.suggestion.title")}</Typography>
                <Typography sx={styles.suggestionText}>
                  {[addressSuggestion.province, addressSuggestion.city, addressSuggestion.address]
                    .filter(Boolean)
                    .join("، ")}
                </Typography>
                <Box sx={styles.suggestionActions}>
                  <Button
                    size="small"
                    variant="contained"
                    fullWidth={false}
                    onClick={onApplyAddressSuggestion}
                  >
                    {t("map.suggestion.apply")}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    fullWidth={false}
                    onClick={onDismissAddressSuggestion}
                    sx={styles.textAction}
                  >
                    {t("map.suggestion.keep")}
                  </Button>
                </Box>
              </Box>
            ) : null}

            <Box sx={styles.formGrid}>
              <FloatingTextField
                label={t("customerInfo.province")}
                autoComplete="address-level1"
                slotProps={{ inputLabel: { shrink: filledFields.province || undefined } }}
                error={Boolean(form.formState.errors.province)}
                helperText={form.formState.errors.province?.message}
                {...form.register("province")}
              />
              <FloatingTextField
                label={t("customerInfo.city")}
                autoComplete="address-level2"
                slotProps={{ inputLabel: { shrink: filledFields.city || undefined } }}
                error={Boolean(form.formState.errors.city)}
                helperText={form.formState.errors.city?.message}
                {...form.register("city")}
              />
            </Box>
            <FloatingTextField
              label={t("customerInfo.address")}
              placeholder={t("customerInfo.addressPlaceholder")}
              multiline
              rows={2}
              autoComplete="street-address"
              slotProps={{ inputLabel: { shrink: filledFields.address || undefined } }}
              error={Boolean(form.formState.errors.address)}
              helperText={form.formState.errors.address?.message}
              {...form.register("address")}
            />
            <FloatingTextField
              label={t("customerInfo.postalCode")}
              inputMode="numeric"
              autoComplete="postal-code"
              slotProps={{ htmlInput: { dir: "ltr", maxLength: 10 } }}
              error={Boolean(form.formState.errors.postal_code)}
              helperText={
                form.formState.errors.postal_code?.message ?? t("customerInfo.postalCodeHint")
              }
              {...form.register("postal_code")}
            />
          </FormSection>

          {errorBox}
          <ActionRow
            onCancel={canCancelDetailsEdit ? onCancelDetailsEdit : undefined}
            cancelLabel={t("customerInfo.cancel")}
          >
            <SefanoButton type="submit" disabled={isSubmitting} fullWidth sx={styles.primaryAction}>
              {isSubmitting ? t("customerInfo.saving") : t("customerInfo.submit")}
            </SefanoButton>
          </ActionRow>
        </Box>
      </>
    ),
    payment: (
      <>
        <StepHeading
          step="payment"
          title={t("payment.title")}
          description={t("payment.description")}
        />
        {savedCustomer ? (
          <Box sx={styles.shippingCard}>
            {savedCustomer.latitude != null && savedCustomer.longitude != null ? (
              <LocationPicker
                readOnly
                height={120}
                value={{ latitude: savedCustomer.latitude, longitude: savedCustomer.longitude }}
              />
            ) : null}
            <Box sx={styles.shippingBody}>
              <Box sx={styles.shippingHeader}>
                <Box sx={styles.shippingTitle}>
                  <Location size={18} variant="Bold" color={theme.palette.primary.main} />
                  {t("payment.shippingTo")}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={onEditDetails}
                  sx={styles.inlineLink}
                >
                  <Edit2 size={14} variant="Linear" color={theme.palette.primary.main} />
                  {t("payment.edit")}
                </Box>
              </Box>
              <Typography sx={styles.shippingName}>
                {savedCustomer.full_name}
                <Box component="span" dir="ltr" sx={styles.shippingPhone}>
                  {savedCustomer.phone_number}
                </Box>
              </Typography>
              <Typography sx={styles.shippingLine}>
                {savedCustomer.province}، {savedCustomer.city}، {savedCustomer.address}
              </Typography>
              {savedShippingMethod ? (
                <Typography sx={styles.shippingLine}>
                  {t("shippingMethod.title")}: {t(`shippingMethod.${savedShippingMethod}`)}
                </Typography>
              ) : null}
              <Typography sx={styles.shippingLine}>
                {t("customerInfo.postalCode")}:{" "}
                <Box component="span" dir="ltr">
                  {savedCustomer.postal_code}
                </Box>
              </Typography>
            </Box>
          </Box>
        ) : null}
        <Box sx={styles.securityNote}>
          <ShieldTick size={18} variant="Bold" color={theme.palette.success.main} />
          {t("payment.securityNote")}
        </Box>
        {errorBox}
        <Box sx={styles.desktopPayWrap}>{renderPayButton()}</Box>
      </>
    ),
  }[step];

  // Where each stepper item can take the customer from the current state.
  const stepTargets: Partial<Record<CheckoutStep, () => void>> = {};
  if (stepIndex > CHECKOUT_STEPS.indexOf("phone")) {
    stepTargets.phone = onEditPhone;
    if (stepIndex > CHECKOUT_STEPS.indexOf("otp")) stepTargets.otp = onEditPhone;
  }
  if (step === "payment") stepTargets.details = onEditDetails;
  if (canCancelDetailsEdit) stepTargets.payment = onCancelDetailsEdit;
  if (canCancelPhoneChange) {
    // Leaving the number change returns to details, or to payment if details are complete.
    stepTargets.details = onCancelPhoneChange;
    if (!checkout.customer_info_required) stepTargets.payment = onCancelPhoneChange;
  }

  const summary = {
    items: checkout.items,
    subtotal: checkout.total_amount,
    shipping: checkout.shipping_cost,
    discount: checkout.discount_amount,
    total: checkout.final_payable_amount,
  };

  return (
    <Box sx={styles.root}>
      <CheckoutHeader />

      <Box component="main" sx={styles.main}>
        <Box sx={styles.mainInner}>
          <Box sx={styles.storeHero}>
            <StoreLogo name={checkout.store.name} logoUrl={checkout.store.logo_url} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>
              <Typography component="h1" sx={styles.heroStoreName}>
                {checkout.store.name}
              </Typography>
              <Typography sx={styles.heroMeta}>
                {t("orderMeta", { orderNumber: formatPersianNumber(checkout.order_number) })}
              </Typography>
            </Box>
            <Box sx={styles.heroAmount}>
              <Typography sx={styles.heroAmountLabel}>{t("payment.total")}</Typography>
              <Typography sx={styles.heroAmountValue}>
                {formatPersianCurrency(checkout.final_payable_amount)}
              </Typography>
            </Box>
          </Box>

          <Box sx={styles.layout}>
            <Box sx={styles.summaryColumn}>
              <SummaryCard {...summary} />
            </Box>
            <Box sx={styles.mainColumn}>
              <Box sx={styles.card("main")}>
                <CheckoutStepper stepIndex={stepIndex} targets={stepTargets} />
                <Box key={step} sx={styles.stepBody}>
                  {stepContent}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {showMobilePayDock ? <Box sx={styles.mobilePayDock}>{renderPayButton()}</Box> : null}
    </Box>
  );
}
